import { useEffect, useState } from 'react';
import { type DiagnosticAnswers, diagnosticNeeds } from '@shared/prospect-diagnostic';
import { ForgeSunArt } from '@/components/composer/ForgeSunArt';
import { QuestionTitle } from '@/components/diagnostic/DiagnosticParts';

/** How long each phrase stays before the next one. */
const PHRASE_MS = 7600;

/**
 * What Domino takes into account, said from the prospect's own answers (Benoit, 03/11): true of
 * the request sent to the model, never a timed step or a percentage.
 */
const phrasesFor = (answers: DiagnosticAnswers): string[] => {
  const needs = diagnosticNeeds(answers);
  const goal = needs.find((need) => need.ref === 'goal');
  const problem = needs.find((need) => need.ref === 'problem');
  const pains = needs.filter((need) => need.ref !== 'goal' && need.ref !== 'problem').slice(0, 3);
  return [
    `Il lit votre site, ${answers.website}, pour parler comme vous.`,
    ...(goal ? [`Il part de vos objectifs : « ${goal.label} ».`] : []),
    ...(problem ? [`Il cherche d’abord comment régler : « ${problem.label} ».`] : []),
    ...pains.map((pain) => `Il cherche une app pour : ${pain.label.charAt(0).toLowerCase()}${pain.label.slice(1)}.`),
    'Il s’inspire de situations commerciales déjà validées avec des équipes.',
    'Il vérifie que chaque point que vous avez cité trouve sa réponse.',
  ];
};

/** The wait for the result: the Forge sun as a loader, then what Domino is weighing, one phrase at a time. */
export const DiagnosticWriting = ({ answers }: { answers: DiagnosticAnswers }) => {
  const phrases = phrasesFor(answers);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((current) => current + 1), PHRASE_MS);
    return () => window.clearInterval(timer);
  }, []);
  const phrase = phrases[index % phrases.length];

  return (
    <div className="flex flex-col items-center gap-6 pt-4 text-center">
      <ForgeSunArt busy className="h-auto w-full max-w-[320px]" />
      <div role="status">
        <QuestionTitle>Domino réfléchit à un premier plan pour vous …</QuestionTitle>
      </div>
      <p key={index} className="min-h-[3.5rem] max-w-lg text-[17px] leading-relaxed text-domino-inkSoft motion-safe:animate-fadeIn">
        {phrase}
      </p>
    </div>
  );
};
